// src/helpers/generateDoc.js
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
pdfMake.vfs = window.pdfMake.vfs;

/** Brand palette */
const BRAND = {
    primary: "#06912b",      // accent
    primarySoft: "#a3f0b7",  // soft background
    ink: "#0F172A",          // text
    subtle: "#64748B",       // secondary text
    line: "#E2E8F0",         // separators
    tableHeader: "#F1F5F9",  // header cells
    zebra: "#ffffff",        // odd rows
};

/** Utilities */
const fmtMoney = (n) => {
    const x = Number(n || 0);
    const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
    return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
const fmtQty = (n) => {
    const s = String(Number(n ?? 0));
    const fixed = Number(s).toFixed(2);
    return fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
};
const toFr = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return isNaN(dt) ? String(d) : dt.toLocaleDateString("fr-MA");
};
function numberToFrenchWords(n) {
    if (n === 0) return "zéro";
    const U = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize"];
    const D = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante", "quatre-vingt", "quatre-vingt"];
    const H = (x) => (x === 0 ? "" : x === 1 ? "cent" : U[x] + " cent" + (x > 1 ? "s" : ""));
    const under100 = (x) => {
        if (x < 17) return U[x];
        const d = Math.floor(x / 10), u = x % 10;
        if (d === 7 || d === 9) return D[d] + (u === 1 ? " et " : "-") + (u ? U[10 + u] : U[10]);
        if (d === 8 && u === 0) return "quatre-vingts";
        if (u === 1 && d >= 1 && d <= 6) return D[d] + " et un";
        return D[d] + (u ? "-" + U[u] : "");
    };
    const under1000 = (x) => {
        const c = Math.floor(x / 100), r = x % 100;
        if (!c) return under100(r);
        return r ? `${H(c)} ${under100(r)}` : H(c);
    };
    let out = "";
    const m = Math.floor(n / 1_000_000);
    const k = Math.floor((n % 1_000_000) / 1000);
    const r = n % 1000;
    if (m) out += (m === 1 ? "un million" : under1000(m) + " millions");
    if (k) out += (out ? " " : "") + (k === 1 ? "mille" : under1000(k) + " mille");
    if (r) out += (out ? " " : "") + under1000(r);
    return out;
}
const amountToWordsMAD = (n) =>
    numberToFrenchWords(Math.round(n || 0)).toUpperCase() + " DIRHAMS";

/** Map seller (name + subtitle centered header) */
function mapSeller(s) {
    return {
        name: s?.companyName || "",
        subtitle: s?.companySubtitle || "",
    };
}

// Build the two footer lines from sellerModel (or seller)
function buildFooterLines(fromSeller) {
    const s = fromSeller || {};
    // line 1: address - phone1[-phone2] - email
    const address = s.companyAddress ? String(s.companyAddress).replace(/\r?\n/g, ", ").trim() : null;
    const phones = [s.companyPhone1, s.companyPhone2].filter(Boolean).join("-");
    const email = s.companyEmail || null;
    const line1 = [address, phones || null, email].filter(Boolean).join(" - ");

    // line 2: RC: x - Patente: y - IF: z - CNSS: w (only show what exists)
    const regs = [
        s.companyIce ? `ICE: ${s.companyIce}` : null,
        s.companyRc ? `RC: ${s.companyRc}` : null,
        s.companyPatente ? `Patente: ${s.companyPatente}` : null,
        s.companyIf ? `IF: ${s.companyIf}` : null,
        s.companyCnss ? `CNSS: ${s.companyCnss}` : null,
    ].filter(Boolean).join(" - ");

    return { line1, regs };
}


/** Map buyer/doc from Matjar payload */
function mapFromMatjar(doc) {

    const items = (doc.lignes || []).map((l) => {
        const qty = Number(l?.quantiteLivree ?? l?.quantite ?? 0);
        const unitTTC = Number(l.prixUnitaireTTC ?? 0);
        const totalHT = Number(l.montantHT);
        const totalTVA = Number(l.montantTVA);
        const totalTTC = Number(l.montantTTC);
        const rawUnitHT = Number(l.prixUnitaireHT ?? 0);
        const unitHT = rawUnitHT > 0
            ? rawUnitHT
            : qty > 0 && totalHT > 0
                ? totalHT / qty
                : rawUnitHT;
        const sub = [l.articleCode, l.unite, l.remise ? `Remise: ${l.remise}` : null]
            .filter(Boolean).join(" • ");
        return {
            label: l.articleNom || "",
            sublabel: sub || undefined,
            qty,
            unit: l?.unite || "",
            unitHT, unitTTC, totalHT, totalTVA, totalTTC,
        };
    });

    const buyer = {
        name: doc.clientNom || "",
        lines: [
            doc.chantierNom || doc.nomChantier ? `Chantier: ${doc.chantierNom || doc.nomChantier}` : null,
            doc.clientAdresse,
            doc.clientIce ? `ICE: ${doc.clientIce}` : null,
            doc.clientTelephone1 ? `Tél: ${doc.clientTelephone1}` : null,
            doc.clientTelephone2 ? `Tél 2: ${doc.clientTelephone2}` : null,
            doc.clientEmail ? `Email: ${doc.clientEmail}` : null,
        ].filter(Boolean),
    };

    const meta = {
        number: doc.numero || "",
        date: toFr(doc.dateFacture || doc.dateOperation || doc.dateCreation),
        payment: doc.modeReglement || undefined,
        transporteur: doc.transporteurName,
    };

    return { items, buyer, meta };
}

/**
 * NEW HEADER LAYOUT:
 * - Top center: Seller name & subtitle
 * - Separator
 * - Below: LEFT rounded card (Numéro/Date/Mode de paiement), RIGHT rounded card (Buyer)
 * - Items table
 * - Rest unchanged (totals, remarks, words, footer)
 */
export async function generateDoc(docType, data = {}) {
    const tMap = {
        devis: "DEVIS",
        bon_commande: "BON DE COMMANDE",
        bon_livraison: "BON DE LIVRAISON",
        facture: "FACTURE",
        recap_document: "RECAP DOCUMENT",
    };

    const {
        // Either pass matjarDoc + sellerModel …
        matjarDoc,
        sellerModel,
        // …or pass pre-mapped:
        seller,
        buyer,
        meta,
        items,
        totals,

        // Options
        priceMode,
        showPrices = docType !== "bon_livraison",
        format = "A4",          // ✅ NOUVEAU
        open = true,
        download = false,
        reglements = [],
    } = data;

    // Build entities
    const s = mapSeller(matjarDoc || seller || {});
    let docBuyer = buyer, docMeta = meta, docItems = items;

    const sellerForFooter = matjarDoc || seller || {};
    const { line1: footerLine1, regs: footerLine2 } = buildFooterLines(sellerForFooter);

    if (matjarDoc) {
        const m = mapFromMatjar(matjarDoc);
        docBuyer = m.buyer;
        docMeta = {
            number: m.meta.number,
            date: m.meta.date,
            payment: m.meta.payment,
        };
        docItems = m.items;
    }

    // Items table rows (TTC by default for display)
    const headerRow = showPrices
        ? docType === 'bon_livraison' ? [
            { text: "#", alignment: "center", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Article", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Quantité", alignment: "center", style: "th", width: 70, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Unité", alignment: "center", style: "th", width: 60, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },   // ✅ NOUVEAU
            { text: "P.U.", alignment: "right", style: "th", width: 80, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Total", alignment: "right", style: "th", width: 90, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
        ] : [
            { text: "#", alignment: "center", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Article", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Quantité", alignment: "center", style: "th", width: 70, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Unité", alignment: "center", style: "th", width: 60, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },   // ✅ NOUVEAU
            { text: priceMode === 'HT' ? "P.U. HT" : "P.U. TTC", alignment: "right", style: "th", width: 80, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: priceMode === 'HT' ? "Total HT" : "Total TTC", alignment: "right", style: "th", width: 90, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
        ]
        : [
            { text: "#", alignment: "center", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Article", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Quantité", alignment: "center", style: "th", width: 70, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Unité", alignment: "center", style: "th", width: 60, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },   // ✅ NOUVEAU
        ];

    const bodyRows = (docItems || []).map((it, i) =>
        showPrices
            ? [
                { text: String(i + 1), alignment: "center" },
                [
                    { text: it.label, bold: true, color: BRAND.ink },
                    it.sublabel ? { text: it.sublabel, color: BRAND.subtle, margin: [0, 2, 0, 0], fontSize: 9 } : "",
                ],
                { text: fmtQty(it.qty), alignment: "center" },
                { text: it.unit || "", alignment: "center" },
                { text: priceMode === 'HT' ? fmtMoney(it.unitHT) : fmtMoney(it.unitTTC), alignment: "right" },
                { text: priceMode === 'HT' ? fmtMoney(it.totalHT) : fmtMoney(it.totalTTC), alignment: "right" },
            ]
            : [
                { text: String(i + 1), alignment: "center" },
                [
                    { text: it.label, bold: true, color: BRAND.ink },
                    it.sublabel ? { text: it.sublabel, color: BRAND.subtle, margin: [0, 2, 0, 0], fontSize: 9 } : "",
                ],
                { text: fmtQty(it.qty), alignment: "center" },
                { text: it.unit || "", alignment: "center" },                      // ✅ NOUVEAU
            ]
    );

    const paymentLabel = (mode) => {
        switch (mode) {
            case "CARTE_BANCAIRE":
                return "Carte bancaire";
            case "ESPECES":
                return "Espèces";
            case "CHEQUE":
                return "Chèque";
            case "VIREMENT":
                return "Virement";
            case "TRAITE":
                return "Traite";
            default:
                return mode || "-";
        }
    };

    const widths = showPrices
        ? (format === "A5"
            ? [18, "*", 50, 45, 60, 65]
            : [22, "*", 70, 60, 80, 90])
        : (format === "A5"
            ? [18, "*", 50, 45]
            : [22, "*", 70, 60]);

    // Totals (unchanged logic)
    const sum = (arr, f) => (arr || []).reduce((s, it) => s + (Number(f(it)) || 0), 0);
    const grandHT = showPrices
        ? Number(totals?.HT ?? matjarDoc?.montantTotalHT ?? sum(docItems, (it) => it.totalHT))
        : 0;
    const grandTVA = showPrices
        ? Number(totals?.TVA ?? matjarDoc?.montantTotalTaxe ?? sum(docItems, (it) => it.totalTVA))
        : 0;
    const grandTTC = showPrices
        ? Number(totals?.TTC ?? matjarDoc?.montantTotalTTC ?? sum(docItems, (it) => it.totalTTC))
        : 0;
    const totalPaye = docType === "facture"
        ? ((reglements || []).reduce(
            (s, reglement) => s + Number(reglement?.montant ?? reglement?.montantRegle ?? 0),
            0
        ) || Number(matjarDoc?.montantRegle || 0))
        : 0;
    const resteAPayer = Math.max(grandTTC - totalPaye, 0);
    const amountValueForWords =
        docType === "facture"
            ? grandTTC
            : priceMode === "HT"
                ? grandHT
                : grandTTC;
    const amountText = amountToWordsMAD(amountValueForWords);
    const dalaSummary = (matjarDoc?.isDala || matjarDoc?.dala) ? [
        { label: "Réf Dala", value: matjarDoc?.refDala ? `#${matjarDoc.refDala}` : "-" },
        { label: "Total ML", value: fmtQty(matjarDoc?.totalML ?? matjarDoc?.totalML ?? 0) },
        { label: "Nombre PTS", value: fmtQty(matjarDoc?.nombrePTS ?? 0) },
        { label: "Nombre Hourdis", value: String(Math.round(Number(matjarDoc?.nombreHourdis ?? 0))) },
        { label: "Nombre Poutrelle", value: fmtQty(matjarDoc?.nombrePourtelle ?? matjarDoc?.nombrePoutrelle ?? 0) },
    ] : null;

    const CARD_W = format === "A5" ? 180 : 250;
    const MIN_CARD_H = format === "A5" ? 90 : 100;
    const PADDING_X = 10;      // inner padding left/right
    const PADDING_Y = 10;      // inner padding top/bottom
    const RADIUS = 8;

    // compute a safe shared height (simple & stable)
    const leftRows = 1 + 3; // title + 3 lines (Numéro, Date, Paiement)
    const rightRows = 1 + 1 + (docBuyer?.lines?.length || 0); // title + name + lines
    const LINE_H = 14;        // approx per line
    const CARD_H = Math.max(MIN_CARD_H, 20 + (Math.max(leftRows, rightRows) * LINE_H));

    const stroke = '#000000';
    const fill = '#FFFFFF';
    const pageSize = format === "A5"
        ? { width: 420, height: 595 }   // A5 portrait (mm → pt pdfMake)
        : "A4";
    const dd = {
        pageSize,
        pageMargins: format === "A5"
            ? [24, 24, 24, 50]
            : [40, 40, 40, 84],
        content: [
            // TOP-CENTER SELLER NAME + SUBTITLE
            {
                stack: [
                    { text: s.name || "", alignment: "center", style: "sellerTopName", margin: [0, 0, 0, 2], color: BRAND.primary },
                    s.subtitle ? { text: s.subtitle, alignment: "center", style: "sellerTopSub", color: BRAND.primary } : null,
                ].filter(Boolean),
                margin: [0, 0, 0, 10],
            },

            // Separator
            { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: BRAND.line }], margin: [0, 6, 0, 10] },

            // TWO CARDS BELOW: LEFT (Doc data) | RIGHT (Buyer)
            // === TWO CARDS BELOW (equal height, black border, white background, rounded corners, gap) ===
            {
                table: {
                    widths: ['*', 12, '*'],
                    body: [[
                        // LEFT CARD
                        {
                            stack: [
                                // 1) draw rounded rect
                                { canvas: [{ type: 'rect', x: 0, y: 0, w: CARD_W, h: CARD_H, r: RADIUS, lineColor: stroke, lineWidth: 1, color: fill }] },
                                // 2) overlay content INTO the rect using negative top margin
                                {
                                    margin: [PADDING_X, -(CARD_H - PADDING_Y) /* pull up */, PADDING_X, 0],
                                    stack: [
                                        { text: (tMap[docType] || "DOCUMENT"), bold: true, margin: [0, 0, 0, 6], color: BRAND.ink },
                                        { text: `Numéro : ${docMeta?.number || "-"}`, color: BRAND.ink },
                                        { text: `Date : ${docMeta?.date || "-"}`, color: BRAND.ink },
                                        (docType === "bon_commande" || docType === "bon_livraison")
                                            ? { text: `Transporteur : ${matjarDoc.transporteurNom || "-"} (${matjarDoc.transporteurMatricule || "-"})`, color: BRAND.ink }
                                            : null,
                                        { text: `Mode paiement : ${paymentLabel(docMeta?.payment ?? matjarDoc?.modeReglement)}`, color: BRAND.ink },

                                    ].filter(Boolean)
                                },
                                // 3) keep cell total height equal to CARD_H
                                { text: '', margin: [0, 15, 0, 0] }
                            ],
                        },

                        // GAP
                        { text: '', border: [false, false, false, false] },

                        // RIGHT CARD
                        {
                            stack: [
                                { canvas: [{ type: 'rect', x: 0, y: 0, w: CARD_W, h: CARD_H, r: RADIUS, lineColor: stroke, lineWidth: 1, color: fill }] },
                                {
                                    margin: [PADDING_X, -(CARD_H - PADDING_Y), PADDING_X, 0],
                                    stack: [
                                        { text: (docType === "bon_livraison" ? "Livrer à" : docType === "recap_document" ? "Client" : "Facturer à"), bold: true, margin: [0, 0, 0, 6], color: BRAND.ink },
                                        { text: (docBuyer?.name || ""), bold: true, color: BRAND.ink, margin: [0, 0, 0, 2] },
                                        ...((docBuyer?.lines || []).map((l) => ({ text: l, color: BRAND.subtle }))),
                                    ]
                                },
                                { text: '', margin: [0, 15, 0, 0] }
                            ],
                        },
                    ]],
                },
                // wrapper table shows no lines; rounded borders are from canvas
                layout: {
                    defaultBorder: false,
                    hLineWidth: () => 0,
                    vLineWidth: () => 0,
                    paddingTop: () => 0,
                    paddingBottom: () => 0,
                    paddingLeft: () => 0,
                    paddingRight: () => 0,
                },
                margin: [0, 10, 0, 1],
            },

            // ITEMS TABLE
            {
                table: {
                    headerRows: 1,
                    widths,
                    body: [headerRow, ...bodyRows],
                },
                layout: {
                    fillColor: (rowIndex) => (rowIndex === 0 ? BRAND.tableHeader : rowIndex % 2 === 0 ? null : BRAND.zebra),
                    hLineWidth: () => 0.6,
                    vLineWidth: () => 0,
                    hLineColor: () => BRAND.line,
                    paddingTop: () => 7,
                    paddingBottom: () => 7,
                },
                margin: [3, 40, 0, 0],
            },

            // === REST OF DOCUMENT UNCHANGED ===

            // Totals card (same visual as before)
            (showPrices ? {
                columns: [
                    { width: "*", text: "" },
                    {
                        width: 220,
                        table: {
                            widths: ["*", 80],
                            body:
                                docType === 'facture'
                                    ? [
                                        [
                                            { text: "Sous-total HT", alignment: "right", color: BRAND.subtle },
                                            { text: fmtMoney(grandHT), alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "TVA (20%)", alignment: "right", color: BRAND.subtle },
                                            { text: fmtMoney(grandTVA), alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                            { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "Paye", alignment: "right", color: BRAND.subtle },
                                            { text: fmtMoney(totalPaye), alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "Reste a payer", bold: true, alignment: "right", color: BRAND.ink },
                                            { text: fmtMoney(resteAPayer), bold: true, alignment: "right", color: BRAND.ink },
                                        ],
                                    ]

                                    : docType === 'recap_document'
                                        ? [
                                            [
                                                { text: "Sous-total HT", alignment: "right", color: BRAND.subtle },
                                                { text: fmtMoney(grandHT), alignment: "right", color: BRAND.ink },
                                            ],
                                            [
                                                { text: "TVA", alignment: "right", color: BRAND.subtle },
                                                { text: fmtMoney(grandTVA), alignment: "right", color: BRAND.ink },
                                            ],
                                            [
                                                { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                                { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                            ],
                                        ]

                                    : (docType === 'devis' || docType === 'bon_commande')
                                        ? priceMode === 'HT'
                                            ? [
                                                [
                                                    { text: "Total HT", bold: true, alignment: "right", color: BRAND.ink },
                                                    { text: fmtMoney(grandHT), bold: true, alignment: "right", color: BRAND.ink },
                                                ],
                                            ]
                                            : [
                                                [
                                                    { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                                    { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                                ],
                                            ]

                                        : [
                                            docType === 'bon_livraison' ? [
                                                    { text: "Total", bold: true, alignment: "right", color: BRAND.ink },
                                                    { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                                ]:[
                                                { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                                { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                            ],
                                        ],
                        },
                        layout: {
                            fillColor: () => BRAND.primarySoft,
                            hLineWidth: () => 0,
                            vLineWidth: () => 0,
                            paddingTop: () => 10,
                            paddingBottom: () => 10,
                            paddingLeft: () => 10,
                            paddingRight: () => 10,
                        },
                        margin: [0, 14, 0, 8],
                    },
                ],
            } : null),

            (docType === "facture" && (reglements || []).length > 0 ? {
                columns: [
                    { width: "*", text: "" },
                    {
                        width: 300,
                        table: {
                            headerRows: 1,
                            widths: [70, 70, 70, 90],
                            body: [
                                [
                                    { text: "Reglement", style: "th", fillColor: BRAND.tableHeader },
                                    { text: "Montant", style: "th", alignment: "right", fillColor: BRAND.tableHeader },
                                    { text: "Mode", style: "th", fillColor: BRAND.tableHeader },
                                    { text: "Numero piece", style: "th", fillColor: BRAND.tableHeader },
                                ],
                                ...(reglements || []).map((reglement) => [
                                    { text: toFr(reglement?.dateReglement), color: BRAND.ink },
                                    { text: fmtMoney(reglement?.montant ?? reglement?.montantRegle), alignment: "right", color: BRAND.ink },
                                    { text: paymentLabel(reglement?.mode ?? reglement?.modeReglement), color: BRAND.ink },
                                    { text: reglement?.numeroPiece || reglement?.referencePaiement || reglement?.reference || "-", color: BRAND.ink },
                                ]),
                            ],
                        },
                        layout: {
                            hLineWidth: () => 0.5,
                            vLineWidth: () => 0,
                            hLineColor: () => BRAND.line,
                            paddingTop: () => 5,
                            paddingBottom: () => 5,
                            paddingLeft: () => 6,
                            paddingRight: () => 6,
                        },
                        margin: [0, 2, 0, 8],
                    },
                ],
            } : null),

            // Amount in words (kept)
            showPrices ? {
                text: `Arrêté le présent ${(tMap[docType] || "document").toLowerCase()} à la somme de : ${amountText}`,
                color: BRAND.subtle,
                fontSize: 9,
                margin: [0, 6, 0, 0],
            } : null,

            (dalaSummary ? {
                width: 260,
                table: {
                    widths: ["*", 90],
                    body: [
                        [{ text: "Résumé Dala", bold: true, colSpan: 2, fillColor: BRAND.primarySoft, color: BRAND.ink }, {}],
                        ...dalaSummary.map((item) => ([
                            { text: item.label, color: BRAND.subtle },
                            { text: item.value, alignment: "right", bold: true, color: BRAND.ink },
                        ])),
                    ],
                },
                layout: {
                    hLineWidth: (i) => (i <= 1 ? 0 : 0.5),
                    vLineWidth: () => 0,
                    hLineColor: () => BRAND.line,
                    paddingTop: () => 8,
                    paddingBottom: () => 8,
                    paddingLeft: () => 10,
                    paddingRight: () => 10,
                },
                margin: [0, 8, 0, 2],
            } : null)
        ].filter(Boolean),

        // Footer (simple, keep or replace with your previous one)
        footer: function (currentPage, pageCount) {
            return {
                margin: [40, 0, 40, 20],
                stack: [
                    // thin separator
                    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#000' }], margin: [0, 4, 0, 6] },

                    // centered company info (from sellerModel)
                    {
                        alignment: 'center',
                        stack: [
                            { text: footerLine1 || "", fontSize: 8, color: '#333' },
                            { text: footerLine2 || "", fontSize: 8, color: '#333', margin: [0, 2, 0, 0] },
                        ],
                    },

                    // (optional) page number, keep or remove as you wish
                    {
                        columns: [
                            { width: '*', text: '' },
                            { width: 'auto', alignment: 'right', text: `Page ${currentPage} / ${pageCount}`, fontSize: 8, color: '#888' },
                        ],
                        margin: [0, 4, 0, 0],
                    },
                ],
            };
        },

        styles: {
            sellerTopName: {
                fontSize: format === "A5" ? 18 : 24,
                bold: true,
                color: "#074e0dff"
            },
            sellerTopSub: { fontSize: 10, color: "#0b5e12ff" },
            th: { bold: true },
            footerLine: { fontSize: 8, color: "#666" },
            footerPage: { fontSize: 8, color: "#888" },
        },
        defaultStyle: { fontSize: format === "A5" ? 9 : 10 },

    };

    const fileName = `${(tMap[docType] || "document")}_${docMetaSafe(docMeta).number}.pdf`;
    if (download) return pdfMake.createPdf(dd).download(fileName);
    if (open) return pdfMake.createPdf(dd).open();
    return dd;

    function docMetaSafe(m) {
        return { number: (m?.number || "").replace(/[^\w/-]/g, "") };
    }
}



